import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Image as ImageIcon } from 'lucide-react';
import { Editor } from '@tinymce/tinymce-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../../components/ui/accordion';
import { useToast } from '../../hooks/use-toast';
import MediaPicker from '../../components/MediaPicker';
import SectionsBuilder from '../../components/admin/SectionsBuilder';
import { invalidateContentBlocks } from '../../hooks/useContentBlock';
import { resolveMediaUrl } from '../../utils/mediaUrl';

const AdminPageForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isEdit = Boolean(id);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const [loading, setLoading] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);
  const [enabledCountries, setEnabledCountries] = useState([]);

  /**
   * Upload a TinyMCE blob through our `/api/media/upload` endpoint,
   * which runs the image through ImageOptimizer and returns
   * `file.optimized.large.jpg.url`. Falls back to the original URL
   * if the variant isn't produced for some reason.
   *
   * Resolves with the full public URL TinyMCE will use as the `<img src>`.
   */
  const tinymceImageUploadHandler = (blobInfo) =>
    new Promise((resolve, reject) => {
      const token = localStorage.getItem('accessToken');
      if (!token) {
        reject({ message: 'Not authenticated', remove: true });
        return;
      }
      const formData = new FormData();
      formData.append('file', blobInfo.blob(), blobInfo.filename());
      formData.append('folder', '/Pages_Content');

      fetch(`${API_URL}/api/media/upload`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      })
        .then(async (res) => {
          if (!res.ok) {
            const text = await res.text().catch(() => '');
            reject({ message: `Upload failed (${res.status}): ${text}`, remove: true });
            return;
          }
          const data = await res.json();
          const optimizedPath = data?.file?.optimized?.large?.jpg?.url;
          const fallbackPath = data?.file?.url;
          const relativePath = optimizedPath || fallbackPath;
          if (!relativePath) {
            reject({ message: 'Upload succeeded but no URL was returned', remove: true });
            return;
          }
          resolve(API_URL + relativePath);
        })
        .catch((err) => reject({ message: err?.message || 'Upload error', remove: true }));
    });

  const [pageCategories, setPageCategories] = useState([]);
  const [expandedAccordions, setExpandedAccordions] = useState([]);
  const editorRef = useRef(null);
  
  const [formData, setFormData] = useState({
    slug: '',
    categoryId: null,
    featuredImage: '',
    isPublished: true,
    publishedAt: null,
    versions: [],
    sections: []
  });

  const getFlagEmoji = (countryCode) => {
    if (!countryCode) return '🌍';
    const codePoints = countryCode.toUpperCase().split('').map(char => 127397 + char.charCodeAt());
    return String.fromCodePoint(...codePoints);
  };

  const generateSlug = (text) => {
    return text.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  useEffect(() => {
    fetchEnabledCountries();
    fetchPageCategories();
  }, []);

  useEffect(() => {
    if (isEdit && enabledCountries.length > 0) {
      fetchPage();
    } else if (!isEdit && enabledCountries.length > 0) {
      initializeFormForCreate();
    }
  }, [id, enabledCountries]);

  const fetchEnabledCountries = async () => {
    try {
      const response = await fetch(`${API_URL}/api/localization/countries`);
      const data = await response.json();
      setEnabledCountries(data);
    } catch (error) {
      console.error('Error fetching countries:', error);
    }
  };

  const fetchPageCategories = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/page-categories/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      setPageCategories(data);
    } catch (error) {
      console.error('Error fetching page categories:', error);
    }
  };

  const initializeFormForCreate = () => {
    const versions = enabledCountries.map(country => ({
      countryCode: country.code,
      language: country.language,
      title: '',
      content: '',
      metaTitle: '',
      metaDescription: '',
      isAvailable: true
    }));

    setFormData({
      slug: '',
      categoryId: null,
      featuredImage: '',
      isPublished: true,
      publishedAt: null,
      versions: versions,
      sections: []
    });
  };

  const fetchPage = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_URL}/api/pages/admin/all`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      const page = data.find(p => (p._id || p.id) === id);
      
      if (page) {
        // Merge existing versions with newly enabled countries
        const existingVersions = page.versions || [];
        const mergedVersions = enabledCountries.map(country => {
          const existingVersion = existingVersions.find(v => v.countryCode === country.code);
          
          if (existingVersion) {
            return existingVersion;
          } else {
            return {
              countryCode: country.code,
              language: country.language,
              title: '',
              content: '',
              metaTitle: '',
              metaDescription: '',
              isAvailable: true
            };
          }
        });
        
        setFormData({
          slug: page.slug || '',
          categoryId: page.categoryId || null,
          featuredImage: page.featuredImage || '',
          isPublished: page.isPublished !== undefined ? page.isPublished : true,
          publishedAt: page.publishedAt || null,
          versions: mergedVersions,
          sections: page.sections || []
        });
      } else {
        toast({
          title: 'Error',
          description: 'Page not found',
          variant: 'destructive'
        });
        navigate('/admin/pages');
      }
    } catch (error) {
      console.error('Error fetching page:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch page',
        variant: 'destructive'
      });
      navigate('/admin/pages');
    }
  };

  const updateVersionField = (versionIndex, field, value) => {
    const newVersions = [...formData.versions];
    newVersions[versionIndex] = {
      ...newVersions[versionIndex],
      [field]: value
    };
    setFormData({ ...formData, versions: newVersions });
  };

  const updateSections = (nextSections) => {
    setFormData({ ...formData, sections: nextSections });
  };

  const handleSave = async () => {
    try {
      if (!formData.slug) {
        toast({
          title: 'Validation Error',
          description: 'Slug is required',
          variant: 'destructive',
        });
        return;
      }

      const hasValidVersion = formData.versions.some(v => v.title && v.title.trim() !== '');
      if (!hasValidVersion) {
        toast({
          title: 'Validation Error',
          description: 'At least one country version with a title is required',
          variant: 'destructive',
        });
        return;
      }

      setLoading(true);
      const token = localStorage.getItem('accessToken');
      let response;

      if (isEdit) {
        response = await fetch(`${API_URL}/api/pages/${id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      } else {
        response = await fetch(`${API_URL}/api/pages`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(formData)
        });
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save page');
      }

      toast({
        title: 'Success',
        description: isEdit ? 'Page updated successfully' : 'Page created successfully',
      });
      // Bust the storefront blocks cache so open tabs pick up the changes.
      invalidateContentBlocks(formData.slug);

      navigate('/admin/pages');
    } catch (error) {
      console.error('Error saving page:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to save page',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (imageUrl) => {
    setFormData({ ...formData, featuredImage: imageUrl });
    setShowMediaPicker(false);
  };

  const getCategoryDisplayName = (category) => {
    return category.versions?.[0]?.name || category.slug || 'Unnamed Category';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button
          variant="outline"
          size="icon"
          onClick={() => navigate('/admin/pages')}
        >
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {isEdit ? 'Edit Page' : 'Add Page'}
          </h1>
          <p className="text-gray-600 mt-1">
            {isEdit ? 'Update page details and country versions' : 'Create a new page with country-specific versions'}
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow p-6 space-y-6">
        {/* Master Page Information */}
        <div className="border-b pb-6">
          <h3 className="text-lg font-semibold mb-4">Master Page Information</h3>
          
          <div className="space-y-4">
            {/* Slug */}
            <div>
              <Label>Slug (URL) *</Label>
              <Input
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value })}
                placeholder="about-us"
              />
            </div>

            {/* Category */}
            <div>
              <Label>Page Category (Optional)</Label>
              <Select
                value={formData.categoryId || 'none'}
                onValueChange={(value) => setFormData({ ...formData, categoryId: value === 'none' ? null : value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="No category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">No Category</SelectItem>
                  {pageCategories.map((cat) => (
                    <SelectItem key={cat.id || cat._id} value={cat.id || cat._id}>
                      {getCategoryDisplayName(cat)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Featured Image */}
            <div>
              <Label>Featured Image</Label>
              <div className="mt-2 space-y-2">
                {formData.featuredImage && (
                  <div className="relative inline-block w-full max-w-md">
                    <img
                      src={resolveMediaUrl(formData.featuredImage)}
                      alt="Featured"
                      className="w-full h-48 object-cover rounded border"
                      onError={(e) => { e.currentTarget.src = 'https://placehold.co/600x192?text=No+image'; }}
                    />
                    <Button
                      type="button"
                      variant="destructive"
                      size="sm"
                      className="absolute top-2 right-2"
                      onClick={() => setFormData({ ...formData, featuredImage: '' })}
                    >
                      Remove
                    </Button>
                  </div>
                )}
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowMediaPicker(true)}
                  className="w-full"
                >
                  <ImageIcon className="h-4 w-4 mr-2" />
                  {formData.featuredImage ? 'Change Image' : 'Select Featured Image'}
                </Button>
              </div>
            </div>

            {/* Published */}
            <div className="flex items-center space-x-2">
              <Checkbox
                checked={formData.isPublished}
                onCheckedChange={(checked) => setFormData({ ...formData, isPublished: checked })}
              />
              <Label className="font-normal cursor-pointer">Published (Visible to public)</Label>
            </div>
          </div>
        </div>

        {/* Country Versions Accordion */}
        <div>
          <h3 className="text-lg font-semibold mb-4">Country Versions</h3>
          <p className="text-sm text-gray-600 mb-4">
            Create content for each country. At least one version with a title is required.
          </p>

          <Accordion type="multiple" value={expandedAccordions} onValueChange={setExpandedAccordions}>
            {formData.versions.map((version, versionIndex) => {
              const country = enabledCountries.find(c => c.code === version.countryCode);
              if (!country) return null;

              return (
                <AccordionItem key={versionIndex} value={`country-${versionIndex}`} className="border rounded-lg mb-2">
                  <AccordionTrigger className="px-4 hover:no-underline">
                    <div className="flex items-center gap-3">
                      <span className="text-2xl">{getFlagEmoji(country.code)}</span>
                      <div className="text-left">
                        <div className="font-semibold">{country.name}</div>
                        <div className="text-sm text-gray-500">
                          {country.languageName}
                          {version.title && <span className="ml-2 text-blue-600">• {version.title}</span>}
                        </div>
                      </div>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="px-4 pb-4">
                    <div className="space-y-4 pt-4">
                      {/* Title */}
                      <div>
                        <Label>Page Title ({country.languageName}) *</Label>
                        <Input
                          value={version.title}
                          onChange={(e) => {
                            updateVersionField(versionIndex, 'title', e.target.value);
                            if (!isEdit && versionIndex === 0 && !formData.slug) {
                              setFormData({ ...formData, slug: generateSlug(e.target.value) });
                            }
                          }}
                          placeholder={`Enter page title in ${country.languageName}`}
                        />
                      </div>

                      {/* Content - TinyMCE WYSIWYG Editor */}
                      <div>
                        <Label>Page Content ({country.languageName})</Label>
                        <div className="mt-2">
                          <Editor
                            apiKey="tnqsmddmqh996y0i53yuugk9kq8ysaekjwlotxp37tmd0wi1"
                            value={version.content}
                            onEditorChange={(content) => updateVersionField(versionIndex, 'content', content)}
                            init={{
                              height: 500,
                              menubar: true,
                              plugins: [
                                'advlist', 'autolink', 'lists', 'link', 'image', 'charmap', 'preview',
                                'anchor', 'searchreplace', 'visualblocks', 'code', 'fullscreen',
                                'insertdatetime', 'media', 'table', 'help', 'wordcount'
                              ],
                              toolbar: 'undo redo | blocks | ' +
                                'bold italic forecolor backcolor | alignleft aligncenter ' +
                                'alignright alignjustify | bullist numlist outdent indent | ' +
                                'link image | removeformat | help',
                              content_style: 'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
                              branding: false,
                              promotion: false,
                              // 🔒 Image optimization: every image inserted, pasted or
                              // drag-dropped into the editor goes through our backend
                              // /api/media/upload endpoint, which runs the file
                              // through ImageOptimizer (≤200 KB large variant) and
                              // returns the optimized URL TinyMCE then embeds.
                              automatic_uploads: true,
                              paste_data_images: true,
                              images_file_types: 'jpeg,jpg,png,webp,gif,avif',
                              images_reuse_filename: false,
                              images_upload_handler: tinymceImageUploadHandler,
                            }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Use the toolbar to format your content. Type naturally and use formatting buttons for styling.
                        </p>
                      </div>

                      {/* SEO Fields */}
                      <div className="border-t pt-4">
                        <h4 className="font-semibold mb-3 text-sm text-gray-700">SEO ({country.name})</h4>
                        
                        <div className="space-y-3">
                          <div>
                            <Label className="text-sm">Meta Title</Label>
                            <Input
                              value={version.metaTitle || ''}
                              onChange={(e) => updateVersionField(versionIndex, 'metaTitle', e.target.value)}
                              placeholder="SEO title (optional)"
                            />
                          </div>

                          <div>
                            <Label className="text-sm">Meta Description</Label>
                            <Textarea
                              value={version.metaDescription || ''}
                              onChange={(e) => updateVersionField(versionIndex, 'metaDescription', e.target.value)}
                              rows={2}
                              placeholder="SEO description (optional)"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Availability */}
                      <div className="flex items-center space-x-2 pt-2">
                        <Checkbox
                          checked={version.isAvailable}
                          onCheckedChange={(checked) => updateVersionField(versionIndex, 'isAvailable', checked)}
                        />
                        <Label className="font-normal cursor-pointer">
                          ✓ Available in {country.name}
                        </Label>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </div>

        {/* Editable Sections (content blocks) */}
        <div className="border-t pt-6">
          <SectionsBuilder
            sections={formData.sections || []}
            enabledCountries={enabledCountries}
            onChange={updateSections}
          />
        </div>

        {/* Actions */}
        <div className="flex gap-4 justify-end pt-6 border-t">
          <Button
            variant="outline"
            onClick={() => navigate('/admin/pages')}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={loading}
            className="bg-[#be1f1a] hover:bg-[#a71b17]"
          >
            {loading ? 'Saving...' : (isEdit ? 'Update Page' : 'Create Page')}
          </Button>
        </div>
      </div>

      {/* Media Picker */}
      <MediaPicker
        isOpen={showMediaPicker}
        onClose={() => setShowMediaPicker(false)}
        onSelect={handleImageSelect}
        fileType="image"
      />
    </div>
  );
};

export default AdminPageForm;
